import React from "react";

/**
 * Label/waarde-rijen voor het "Gegevens"-blok in de zijkolom.
 *
 * Bewust géén `Descriptions`: die dwingt een kolomraster af en werd op de
 * detailpagina's gebruikt om twintig velden naast elkaar te proppen. Eén veld
 * per regel, waarde rechts uitgelijnd, leest sneller in een smalle kolom.
 *
 * Drie dingen die de indeling in een kolom van ~274px overeind houden:
 *
 * 1. Het labelspoor is een ONDERGRENS (`minmax(labelWidth, min-content)`), geen
 *    vaste maat. Een label van één lang woord ("Containernummer", ~119px) liep
 *    anders buiten zijn 108px-spoor en at de gap op, waardoor label en waarde
 *    tegen elkaar aan kwamen te staan. Korte labels houden exact hun oude
 *    breedte; meerwoordige labels breken nog steeds gewoon af.
 * 2. `action` is een EIGEN slot in plaats van iets dat de aanroeper zelf naast
 *    de waarde propt. Een niet-krimpbare "Bewerken"-knop in dezelfde cel at
 *    ~40% van de waardekolom op, en stond door de verticale centrering midden
 *    naast een meerregelige waarde in plaats van bij de eerste regel.
 * 3. `emptyAction` is wat er in plaats van een streepje komt als het veld leeg
 *    is — zo is een niet-ingevuld container- of trackingnummer meteen invulbaar.
 * 4. `stacked` zet het label op een eigen regel met de waarde eronder over de
 *    volle breedte. Bedoeld voor een INVOERVELD: een tekstveld met twee knoppen
 *    ernaast houdt naast een label van ~119px nog geen 60px over, waardoor je
 *    niet meer kunt zien wat je typt. Een waarde die alleen gelezen wordt hoort
 *    gewoon naast zijn label te blijven staan — zet `stacked` dus aan zolang het
 *    veld in bewerkmodus staat, niet permanent.
 *
 * Items: `{ label, value, action, emptyAction, wrap, stacked }`. Met `wrap: true`
 * mag de waarde over meerdere regels; standaard blijft hij op één regel met
 * ellipsis, omdat een blok tekst in een smalle zijkolom onleesbaar is. In
 * `stacked`-modus is `wrap` overbodig (de waarde heeft dan de volle breedte) en
 * schuift `action` mee naar rechts op de labelregel.
 */
const KeyValueList = ({ items = [], labelWidth = 108 }) => {
  const visible = items.filter(Boolean);

  return (
    <div style={{ padding: "6px 20px 14px" }}>
      {visible.map((item, index) => {
        const hasValue = item.value !== null && item.value !== undefined && item.value !== "";
        const action = hasValue ? item.action : (item.emptyAction ?? item.action);
        const borderBottom = index === visible.length - 1 ? undefined : "1px solid rgba(5, 5, 5, 0.04)";

        // Label boven, waarde eronder over de volle breedte — zie punt 4 hierboven.
        if (item.stacked) {
          return (
            <div key={item.label} style={{ padding: "9px 0", borderBottom }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>{item.label}</span>
                {action ? <div style={{ flex: "none", whiteSpace: "nowrap" }}>{action}</div> : null}
              </div>
              <div style={{ fontSize: 13, minWidth: 0 }}>{item.value}</div>
            </div>
          );
        }

        return (
          <div
            key={item.label}
            style={{
              display: "grid",
              // Ondergrens, niet vaste maat — zie punt 1 hierboven.
              gridTemplateColumns: `minmax(${labelWidth}px, min-content) minmax(0, 1fr)`,
              gap: 12,
              alignItems: "start",
              padding: "9px 0",
              borderBottom,
            }}
          >
            <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)", minWidth: 0, paddingTop: 1 }}>
              {item.label}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                gap: 6,
                minWidth: 0,
                fontSize: 13,
              }}
            >
              <div
                style={{
                  minWidth: 0,
                  textAlign: "right",
                  ...(item.wrap
                    ? { overflowWrap: "anywhere" }
                    : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
                }}
              >
                {hasValue ? item.value : <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>}
              </div>
              {/* Actie hangt altijd tegen de rechterrand en krimpt niet mee. */}
              {action ? <div style={{ flex: "none", whiteSpace: "nowrap" }}>{action}</div> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KeyValueList;
